<template>
  <div class="image-attachment" ref="containerRef">
    <div class="image-attachment__wrapper">
      <div v-if="!imageLoaded" class="image-attachment__placeholder" @click="loadImage">
        <div v-if="isLoading" class="image-attachment__progress-container">
          <ProgressCircle
            :progress="loadingProgress"
            size="medium"
            bg-stroke="rgba(255, 255, 255, 0.2)"
            fill-stroke="white"
          />
        </div>
        <div v-else class="image-attachment__icon-container">
          <Icon icon="majesticons:arrow-down" class="image-attachment__preview-icon" />
        </div>
        <span>{{ isLoading ? "Loading..." : "Click to preview" }}</span>
      </div>
      <img
        v-if="imageLoaded && resolvedImageUrl"
        :src="resolvedImageUrl"
        :alt="attachment.name || 'Image'"
        class="image-attachment__img"
        @error="handleError"
        @click="handleClick"
        @load="handleLoad"
      />
      <div v-if="imageError && imageLoaded && !resolvedImageUrl" class="image-attachment__error">
        <Icon icon="mdi:image-off" />
        <span>Failed to load image</span>
      </div>
      <div
        v-if="attachment.sticker"
        class="image-attachment__badge image-attachment__badge--sticker"
      >
        <Icon icon="mdi:emoticon" />
        <span>Sticker</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import type { Attachment } from "@/dtos/Unipile.dto";
import { Icon } from "@iconify/vue";
import { useAttachmentLoader } from "@/composables/attachments/useAttachmentLoader";
import ProgressCircle from "./ProgressCircle.vue";

interface Props {
  attachment: Attachment;
  messageId?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  imageClick: [url: string, attachment: Attachment];
}>();

const containerRef = ref<HTMLElement | null>(null);
const imageError = ref(false);
const imageLoaded = ref(false);
let intersectionObserver: IntersectionObserver | null = null;

const { isLoading, loadingProgress, blobUrl, loadAttachment, getAttachmentUrl } =
  useAttachmentLoader(props.attachment, props.messageId, () => {});

const resolvedImageUrl = computed(() => {
  if (blobUrl.value) return blobUrl.value;
  return getAttachmentUrl(props.attachment.url);
});

const loadImage = async () => {
  if (!props.attachment.url) return;

  if (props.attachment.url.startsWith("att://") && props.messageId && props.attachment.id) {
    const blob = await loadAttachment("image/jpeg");
    if (blob) {
      imageError.value = false;
      imageLoaded.value = true;
    } else {
      imageError.value = true;
      imageLoaded.value = false;
    }
  } else {
    imageLoaded.value = true;
  }
};

const handleClick = async () => {
  let url = resolvedImageUrl.value;

  if (
    !blobUrl.value &&
    props.attachment.url?.startsWith("att://") &&
    props.messageId &&
    props.attachment.id
  ) {
    const blob = await loadAttachment("image/jpeg");
    if (blob) {
      url = blobUrl.value!;
    }
  }

  if (url) {
    emit("imageClick", url, props.attachment);
  }
};

const handleError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  if (img && img.src && img.src !== resolvedImageUrl.value) {
    return;
  }
  imageError.value = true;
  imageLoaded.value = false;
};

const handleLoad = () => {
  imageError.value = false;
};

const setupLazyLoading = () => {
  if (
    props.attachment.type === "img" &&
    props.attachment.url?.startsWith("att://") &&
    props.messageId &&
    props.attachment.id &&
    !imageLoaded.value &&
    !isLoading.value
  ) {
    if ("IntersectionObserver" in window && containerRef.value) {
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !imageLoaded.value && !isLoading.value) {
              loadImage();
              if (intersectionObserver && containerRef.value) {
                intersectionObserver.unobserve(containerRef.value);
              }
            }
          });
        },
        { rootMargin: "50px" }
      );
      intersectionObserver.observe(containerRef.value);
    } else {
      loadImage();
    }
  }
};

watch(
  () => [props.attachment.id, props.messageId, props.attachment.url],
  () => {
    if (props.attachment.type === "img" && !props.attachment.url?.startsWith("att://")) {
      imageError.value = false;
      imageLoaded.value = true;
    }
  },
  { immediate: false }
);

watch(
  () => resolvedImageUrl.value,
  (url) => {
    if (url && !props.attachment.url?.startsWith("att://")) {
      imageLoaded.value = true;
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (props.attachment.type === "img" && !props.attachment.url?.startsWith("att://")) {
    imageLoaded.value = true;
  } else if (props.attachment.type === "img" && props.attachment.url?.startsWith("att://")) {
    setTimeout(() => {
      setupLazyLoading();
    }, 100);
  }
});

onUnmounted(() => {
  if (intersectionObserver && containerRef.value) {
    intersectionObserver.unobserve(containerRef.value);
    intersectionObserver.disconnect();
  }
});
</script>

<style scoped>
.image-attachment {
  position: relative;
  max-width: 300px;
}

.image-attachment__wrapper {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  background: #f3f4f6;
}

.image-attachment__wrapper:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.image-attachment__placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2rem;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px dashed #d1d5db;
  color: #6b7280;
  font-size: 0.875rem;
  min-height: 120px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.image-attachment__placeholder:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
  color: #111827;
}

.image-attachment__progress-container {
  position: relative;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-attachment__icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  border-radius: 50%;
  transition: all 0.2s ease;
}

.image-attachment__placeholder:hover .image-attachment__icon-container {
  background: rgba(0, 0, 0, 0.7);
  transform: scale(1.05);
}

.image-attachment__preview-icon {
  font-size: 1.5rem;
  color: #ffffff;
  transition: all 0.2s ease;
}

.image-attachment__placeholder:hover .image-attachment__preview-icon {
  transform: translateY(2px);
}

.image-attachment__img {
  width: 100%;
  height: auto;
  display: block;
  cursor: pointer;
  max-height: 400px;
  object-fit: cover;
}

.image-attachment__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem;
  color: #9ca3af;
  font-size: 0.875rem;
}

.image-attachment__error svg {
  font-size: 2rem;
  opacity: 0.5;
}

.image-attachment__badge {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.625rem;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  color: white;
  border-radius: 8px;
  font-size: 0.6875rem;
  font-weight: 600;
  z-index: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.image-attachment__badge--sticker {
  background: rgba(0, 0, 0, 0.75);
}
</style>
